import api from "./api";

export type TipoEvento = "aniversario" | "feriado" | "reuniao" | "treinamento" | "podcast" | "outro";

export interface EventoCalendario {
  id: string | null;          // null para feriados externos
  titulo: string;
  descricao?: string | null;
  data: string;               // ISO date "YYYY-MM-DD"
  hora?: string | null;       // "HH:MM" quando o evento tem horário específico
  tipo: TipoEvento;
  recorrenteAnual?: boolean;
  colaboradorId?: string | null;
  origem: "interno" | "feriado-api";
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export async function listarEventosCalendario(inicio: Date, fim: Date) {
  const params = new URLSearchParams();
  params.append("inicio", ymd(inicio));
  params.append("fim", ymd(fim));
  const r = await api.get(`/calendario/eventos?${params.toString()}`);
  return r.data.data as EventoCalendario[];
}

export async function criarEventoCalendario(data: {
  titulo: string;
  descricao?: string | null;
  data: string;               // YYYY-MM-DD
  tipo: TipoEvento;
  recorrenteAnual?: boolean;
  colaboradorId?: string | null;
}) {
  const r = await api.post("/calendario/eventos", data);
  return r.data.data;
}

export async function atualizarEventoCalendario(id: string, data: Partial<{
  titulo: string;
  descricao: string | null;
  data: string;
  tipo: TipoEvento;
  recorrenteAnual: boolean;
  colaboradorId: string | null;
}>) {
  const r = await api.patch(`/calendario/eventos/${id}`, data);
  return r.data.data;
}

export async function deletarEventoCalendario(id: string) {
  await api.delete(`/calendario/eventos/${id}`);
}

export async function seedAniversarios() {
  const r = await api.post("/calendario/eventos/seed-aniversarios");
  return r.data.data as { criados: number; jaExistiam: number };
}

export interface AniversarioItem {
  id: string;
  titulo: string;
  descricao: string | null;
  dia: number;
  mes: number;
  colaboradorId: string | null;
  colaboradorNome: string | null;
}

export async function listarAniversarios(): Promise<AniversarioItem[]> {
  // Tenta o endpoint dedicado primeiro (com colaboradorNome). Se não estiver disponível
  // (backend rodando build antigo), cai pra fallback via /eventos cobrindo o ano corrente.
  try {
    const r = await api.get("/calendario/aniversarios");
    return r.data.data as AniversarioItem[];
  } catch (e: any) {
    if (e?.response?.status !== 404) throw e;
  }
  const ano = new Date().getFullYear();
  const inicio = new Date(ano, 0, 1);
  const fim = new Date(ano, 11, 31, 23, 59, 59);
  const eventos = await listarEventosCalendario(inicio, fim);
  return eventos
    .filter((ev) => ev.tipo === "aniversario" && ev.id)
    .map((ev) => {
      const d = new Date(`${ev.data}T12:00:00`);
      return {
        id: ev.id!,
        titulo: ev.titulo,
        descricao: ev.descricao ?? null,
        dia: d.getDate(),
        mes: d.getMonth() + 1,
        colaboradorId: ev.colaboradorId ?? null,
        colaboradorNome: null,
      } as AniversarioItem;
    });
}

// =============================================================================
// Preferências de lembrete (locais, persistidas em localStorage)
// =============================================================================
const KEY_LEMBRETE_ATIVO = "calendario_lembrete_ativo";
const KEY_LEMBRETE_ANTECEDENCIA = "calendario_lembrete_antecedencia_min";

export function getLembreteAtivo(): boolean {
  try { return localStorage.getItem(KEY_LEMBRETE_ATIVO) === "1"; } catch { return false; }
}
export function setLembreteAtivo(v: boolean) {
  try { localStorage.setItem(KEY_LEMBRETE_ATIVO, v ? "1" : "0"); } catch {}
}
export function getLembreteAntecedencia(): number {
  try {
    const v = parseInt(localStorage.getItem(KEY_LEMBRETE_ANTECEDENCIA) || "15", 10);
    return Number.isFinite(v) && v > 0 ? v : 15;
  } catch { return 15; }
}
export function setLembreteAntecedencia(min: number) {
  try { localStorage.setItem(KEY_LEMBRETE_ANTECEDENCIA, String(Math.max(1, Math.floor(min)))); } catch {}
}

export const ANTECEDENCIA_OPCOES: Array<{ valor: number; label: string }> = [
  { valor: 5, label: "5 minutos antes" },
  { valor: 15, label: "15 minutos antes" },
  { valor: 30, label: "30 minutos antes" },
  { valor: 60, label: "1 hora antes" },
  { valor: 120, label: "2 horas antes" },
  { valor: 1440, label: "1 dia antes" },
];

// Estilo visual por tipo de evento
export const EVENTO_VISUAL: Record<TipoEvento, { emoji: string; label: string; cor: string }> = {
  aniversario:  { emoji: "🎂", label: "Aniversário",  cor: "bg-pink-100 text-pink-700 border-pink-300 dark:bg-pink-900/40 dark:text-pink-300 dark:border-pink-700" },
  feriado:      { emoji: "🏖️", label: "Feriado",      cor: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700" },
  reuniao:      { emoji: "📅", label: "Reunião",      cor: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700" },
  treinamento:  { emoji: "🎓", label: "Treinamento",  cor: "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700" },
  podcast:      { emoji: "🎙️", label: "Podcast",      cor: "bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-700" },
  outro:        { emoji: "📌", label: "Outro",        cor: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600" },
};
